import React, { createContext } from 'react';
import styled from 'styled-components';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

import { elevationTokens } from '../tokens/globals/tokens';

export interface ContextType {
  components: Array<() => React.ReactElement>;
  props: any;
  // eslint-disable-next-line @typescript-eslint/ban-types
  addAlert: (component: Function, props?: any) => void;
  removeAlert: (component: React.ReactElement | string, props?: any) => void;
}

const AlertContext = createContext<ContextType>({
  components: [],
  props: {},
  addAlert: () => {},
  removeAlert: () => {},
});

const AlertConsumer = AlertContext.Consumer;

const withAlertContext = <T extends React.FC<any>>(Component: T) => (props: React.ComponentProps<T>) => (
  <AlertConsumer>
    {({ addAlert, removeAlert }) => (
      <Component {...props} addAlert={addAlert} removeAlert={() => removeAlert(props.id)} />
    )}
  </AlertConsumer>
);

const AlertContextWrapper = styled.div`
  position: fixed;
  right: 20px;
  top: 70px;
  z-index: ${elevationTokens.GlobalAlert};

  // This is migrated for parity. The breakpoints are different in
  // our sass files so we'll revisit this in a different PR when
  // we add more support for responsiveness in tokens.
  @media (max-width: 766px) {
    left: 0;
    right: 0;
    top: 0;
  }
`;

function AlertRoot() {
  // CSS animation duration
  // This variable has to be equal to the
  // transition-duration value inside `_alert.scss`
  const trasitionTimeout = 130;

  return (
    <AlertConsumer>
      {({ components, props, removeAlert }) => (
        <AlertContextWrapper>
          <TransitionGroup>
            {components.map(Component => (
              <CSSTransition
                key={Component().props.id}
                classNames={{
                  enter: 'enter',
                  enterActive: 'enter-active',
                  exit: 'exit',
                  exitActive: 'exit-active',
                }}
                timeout={trasitionTimeout}
              >
                <Component {...props} removeAlert={() => removeAlert(Component().props.id)} />
              </CSSTransition>
            ))}
          </TransitionGroup>
        </AlertContextWrapper>
      )}
    </AlertConsumer>
  );
}

class AlertProvider extends React.Component<any, ContextType> {
  constructor(props) {
    super(props);

    this.addAlert = this.addAlert.bind(this);
    this.removeAlert = this.removeAlert.bind(this);

    this.state = {
      components: [],
      props: {},
      addAlert: this.addAlert,
      removeAlert: this.removeAlert,
    };
  }

  addAlert(component, props = {}) {
    this.setState(prevState => {
      let prevComponents = prevState.components;

      if (component().props.type === 'download') {
        prevComponents = prevComponents.filter(comp => comp().props.type !== 'generating');
      }

      return {
        components: [component, ...prevComponents],
        props,
      };
    });
  }

  removeAlert(id) {
    this.setState(prevState => ({
      components: prevState.components.filter(comp => comp().props.id !== id),
      props: {},
    }));
  }

  render() {
    return (
      <AlertContext.Provider value={this.state}>
        <AlertRoot />
        {this.props.children}
      </AlertContext.Provider>
    );
  }
}

export { AlertContext, AlertConsumer, withAlertContext, AlertRoot, AlertProvider };
